import { leftPad } from '../utils/string'
import { differenceInMinutes } from 'date-fns'

/**
 * A countdown component that takes either two timestamps (in milliseconds) and counts the duration of those 2,
 * or takes an end timestamp and counts down from current present time.
 */
export default {
  name: 'CountDown',
  props: {
    start: Number,
    end: {
      type: Number,
      required: true,
    },
    name: String,
    offset: {
      default: 0,
      type: Number,
    },
    customCountDownStep: Number,
    countDownText: String,
    enableBloopAnimations: Boolean,
  },
  data() {
    let startTimeStamp = this.getStartTimeStamp()

    return {
      endTimeStamp: this.end + this.offset,
      startTimeStamp: startTimeStamp,
      countDownPosition: startTimeStamp,
      countDownStep: 1000,
      countDownInterval: null,
      countDownTimeout: null,
      doBloopAnimation: false,
      countDownFinished: false,
    }
  },
  mounted() {
    if (this.getCurrentTime() <= this.startTimeStamp) {
      this.countDownTimeout = setTimeout(() => {
        this.startCountDown()
        clearTimeout(this.countDownTimeout)
      }, this.startTimeStamp - this.getCurrentTime())
    } else {
      this.startCountDown()
    }
  },

  methods: {
    /**
     *
     * @returns {number}
     */
    getStartTimeStamp() {
      let nowStamp = this.getCurrentTime()

      return (this.start || nowStamp) + this.offset
    },
    startCountDown() {
      this.countDownInterval = setInterval(() => {
        if (this.countDownPosition >= this.endTimeStamp) {
          this.$emit('finished')
          this.countDownFinished = true
          clearInterval(this.countDownInterval)
          return

          // Is there is less time than the countDown step left, we'd like to finish then.
        } else if (this.finishBeforeCountDownStep()) {
          clearInterval(this.countDownInterval)
          setTimeout(() => {
            this.countDownFinished = true
            this.$emit('finished')
            this.countDownPosition = this.getCurrentTime()
          }, this.countDownPosition - this.endTimeStamp)
          return
        }

        this.countDownPosition = this.getCurrentTime()
      }, this.countDownStep)
    },

    /**
     * @param {number} startTimeStamp
     * @param {number} endTimeStamp
     * @returns {number} minutes
     */
    remainingMinutes(startTimeStamp, endTimeStamp) {
      return Math.max(differenceInMinutes(endTimeStamp, startTimeStamp), 0)
    },
    /**
     * @param {number} startTimeStamp
     * @param {number} endTimeStamp
     * @returns {string} seconds
     */
    remainingSeconds(startTimeStamp, endTimeStamp) {
      return leftPad(
        Math.max(
          Math.floor(
            (this.remainingDuration(startTimeStamp, endTimeStamp) % 60000) /
              1000
          ),
          0
        ),
        2
      )
    },
    /**
     * @param {number} startTimeStamp
     * @param {number} endTimeStamp
     * @returns {number} duration in milliseconds
     */
    remainingDuration(startTimeStamp, endTimeStamp) {
      return endTimeStamp - startTimeStamp
    },

    /**
     * @returns {number} milliseconds
     */
    getCurrentTime() {
      return new Date().getTime()
    },
    /**
     * gets the time representation of the current count down
     * @param {milliseconds} startTimeStamp
     * @param {milliseconds} endTimeStamp
     * @returns {string} Ex: 11:11
     */
    getTimeDisplay(startTimeStamp, endTimeStamp) {
      const remainingMinutes = this.remainingMinutes(
        startTimeStamp,
        endTimeStamp
      )

      const remainingSeconds = this.remainingSeconds(
        startTimeStamp,
        endTimeStamp
      )

      if (
        this.enableBloopAnimations &&
        remainingMinutes === 0 &&
        remainingSeconds <= 3 &&
        !this.countDownFinished
      ) {
        // Does a "bloop" animation on every count down, 3 seconds before finish time, to give a sense of urgency.
        this.doBloopAnimation = true
        setTimeout(() => {
          this.doBloopAnimation = false
        }, 150)
      }

      return `${remainingMinutes}:${remainingSeconds}`
    },
    finishBeforeCountDownStep() {
      return this.countDownStep > this.endTimeStamp - this.countDownPosition
    },
  },
  computed: {
    countDownDisplay() {
      return this.getTimeDisplay(this.countDownPosition, this.endTimeStamp)
    },
  },
}
